import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    ShieldCheck, UserCircle, MapPin, Tag,
    CheckCircle2, ChevronUp, ChevronDown, X, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { ROLES } from "@/lib/roles";
import { providerKycApi } from "@/lib/api/kyc";
import { providerApi } from "@/lib/api/provider";
import { providerOffersApi } from "@/lib/api/offers";

// Ring SVG — shows percentage as a circular stroke
// trackColor / fillColor are hex/rgba strings so they work on any background
function Ring({ pct, size = 36, stroke = 3.5, trackColor = "rgba(99,102,241,0.2)", fillColor = "rgb(99,102,241)" }) {
    const r = (size - stroke * 2) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (pct / 100) * circ;
    return (
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} className="shrink-0">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke={trackColor} strokeWidth={stroke} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke={fillColor} strokeWidth={stroke}
                strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
                style={{ transition: "stroke-dasharray 0.5s ease" }} />
        </svg>
    );
}

export default function SetupProgressWidget() {
    const { user } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();

    const [open, setOpen]         = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [steps, setSteps]       = useState([]);
    const [loaded, setLoaded]     = useState(false);

    const load = useCallback(async () => {
        try {
            const [kyc, provider, offers, locations] = await Promise.all([
                providerKycApi.summary(),
                providerApi.me(),
                providerOffersApi.list().catch(() => []),
                providerApi.listLocations().catch(() => []),
            ]);
            const kycStatus  = kyc?.provider?.kyc_status || "DRAFT";
            setSteps([
                {
                    done:   kycStatus === "APPROVED" || kycStatus === "PENDING",
                    icon:   ShieldCheck,
                    label:  t("providerDash.stepKycTitle"),
                    action: t("providerDash.stepKycAction"),
                    to:     "/provider/kyc",
                },
                {
                    done:   !!(provider?.description_en || provider?.description_ar || provider?.logo_url),
                    icon:   UserCircle,
                    label:  t("providerDash.stepProfileTitle"),
                    action: t("providerDash.stepProfileAction"),
                    to:     "/provider/profile",
                },
                {
                    done:   locations.length > 0,
                    icon:   MapPin,
                    label:  t("providerDash.stepLocationTitle"),
                    action: t("providerDash.stepLocationAction"),
                    to:     "/provider/locations",
                },
                {
                    done:   offers.length > 0,
                    icon:   Tag,
                    label:  t("providerDash.stepOfferTitle"),
                    action: t("providerDash.stepOfferAction"),
                    to:     "/provider/offers",
                },
            ]);
        } catch (_) {}
        setLoaded(true);
    }, [t]);

    useEffect(() => {
        if (user?.role === ROLES.PROVIDER_ADMIN) load();
    }, [user, load]);

    // Don't render for non-provider-admins, before data loads, or if dismissed
    if (!loaded || user?.role !== ROLES.PROVIDER_ADMIN || dismissed) return null;

    const done  = steps.filter(s => s.done).length;
    const total = steps.length;
    const pct   = total ? Math.round((done / total) * 100) : 0;
    const allDone = done === total;

    // Auto-hide after all done (user can still see it while open)
    if (allDone && !open) return null;

    return (
        <div
            className="fixed bottom-6 end-6 z-50 flex flex-col items-end gap-2"
            data-testid="setup-progress-widget"
        >
            {/* Expanded panel */}
            {open && (
                <div className="w-72 rounded-2xl border border-border bg-card shadow-xl overflow-hidden animate-in slide-in-from-bottom-2 fade-in duration-200">

                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/40">
                        <div className="flex items-center gap-2">
                            <Ring pct={pct} size={28} stroke={3} />
                            <span className="text-sm font-semibold">
                                {t("providerDash.setupTitle")}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground me-1">
                                {done}/{total}
                            </span>
                            <button
                                onClick={() => setDismissed(true)}
                                className="h-6 w-6 rounded-lg flex items-center justify-center hover:bg-accent transition-colors text-muted-foreground"
                                aria-label="Dismiss"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>

                    {/* Steps */}
                    <div className="p-2 space-y-0.5">
                        {steps.map((step, i) => (
                            <button
                                key={i}
                                onClick={() => { if (!step.done) navigate(step.to); }}
                                disabled={step.done}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start transition-colors
                                    ${step.done
                                        ? "opacity-50 cursor-default"
                                        : "hover:bg-accent cursor-pointer group"
                                    }`}
                            >
                                <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0
                                    ${step.done ? "bg-green-100 dark:bg-green-950" : "bg-muted"}`}>
                                    {step.done
                                        ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        : <step.icon className="h-3.5 w-3.5 text-muted-foreground" />
                                    }
                                </div>
                                <span className={`flex-1 text-sm font-medium ${step.done ? "line-through text-muted-foreground" : ""}`}>
                                    {step.label}
                                </span>
                                {!step.done && (
                                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Progress bar */}
                    <div className="px-4 pb-3 pt-1">
                        <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
                            <div
                                className="h-full rounded-full bg-primary transition-all duration-500"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* FAB toggle button */}
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2.5 rounded-2xl bg-primary px-4 py-2.5 text-primary-foreground shadow-lg hover:opacity-90 active:scale-95 transition-all"
                data-testid="setup-widget-toggle"
            >
                <Ring pct={pct} size={24} stroke={3} trackColor="rgba(255,255,255,0.3)" fillColor="white" />
                <span className="text-sm font-semibold">
                    {done}/{total} {t("providerDash.setupDone")}
                </span>
                {open
                    ? <ChevronDown className="h-3.5 w-3.5 opacity-80" />
                    : <ChevronUp className="h-3.5 w-3.5 opacity-80" />
                }
            </button>
        </div>
    );
}
